import React from "react";
import { Link } from "react-router-dom";
import { Tab } from "@headlessui/react";
import classNames from "@/utils/helpers/ClassesUtils";
import Spinner from "@/components/loader/Spinner";
import Button from "@/components/ui/Button";
import CloseIcon from "@/assets/img/CloseIcon";
import { Branch } from "@/types/branch";
import { CartItem } from "@/types/cart";

interface CartItemsViewProps {
  branch: Branch | undefined;
  cart: CartItem[];
  adjustedCart: CartItem[];
  hasServiceInCart: boolean;
  orderMode: string;
  subtotal: number;
  loading: boolean;
  merchantSlug: string;
  setOrderMode: (mode: string) => void;
  handleCartClose?: () => void;
  handleClearCart: () => void;
  handleQuantityChange: (itemId: string, increment: boolean) => void;
  handleRemove: (uniqueId: string) => void;
  handleCheckoutButtonClick: () => void;
  productPrice: (item: CartItem) => string;
  calculateItemTotal: (item: CartItem) => string;
}

export const CartItemsView: React.FC<CartItemsViewProps> = ({
  branch,
  cart,
  adjustedCart,
  hasServiceInCart,
  orderMode,
  subtotal,
  loading,
  merchantSlug,
  setOrderMode,
  handleCartClose,
  handleClearCart,
  handleQuantityChange,
  handleRemove,
  handleCheckoutButtonClick,
  productPrice,
  calculateItemTotal,
}) => {
  return (
    <div className="cart-page overflow-y-auto max-h-screen pb-2">
      {loading && (
        <div className="loading-overlay">
          <Spinner />
        </div>
      )}

      <div className="w-full items-center">
        <div className="header w-full">
          <h1 className="text-2xl md:text-3xl font-bold">{branch?.name}</h1>
          <button
            onClick={() => {
              handleCartClose?.();
              if (hasServiceInCart) {
                // Services are checked out directly so clearing the cart
                handleClearCart();
              }
            }}
          >
            <Link to={`${merchantSlug}`}>
              <CloseIcon />
            </Link>
          </button>
        </div>

        <div className="tab-list px-4">
          {hasServiceInCart ? (
            <Tab.Group>
              <Tab.List className="flex tab-list justify-center space-x-4 rounded-full border border-redCrimson p-1 cart-width">
                <Tab
                  className={({ selected }) =>
                    classNames(
                      "tab px-8 py-2 text-sm font-medium rounded-full transition-colors duration-150 ease-in-out",
                      selected
                        ? "bg-redCrimson text-white shadow-md"
                        : "bg-white text-redCrimson hover:bg-red-50",
                    )
                  }
                  onClick={() => setOrderMode("Pick up")}
                >
                  In-Store Fullfillment
                </Tab>
              </Tab.List>
            </Tab.Group>
          ) : branch?.delivery_feature ? (
            <Tab.Group selectedIndex={orderMode === "Delivery" ? 1 : 0}>
              <Tab.List className="flex tab-list justify-center space-x-4 rounded-full border border-redCrimson p-1 cart-width">
                <Tab
                  className={({ selected }) =>
                    classNames(
                      "tab px-8 py-2 text-sm font-medium rounded-full transition-colors duration-150 ease-in-out",
                      selected
                        ? "bg-redCrimson text-white shadow-md"
                        : "bg-white text-redCrimson hover:bg-red-50",
                    )
                  }
                  onClick={() => setOrderMode("Pick up")}
                >
                  Pickup
                </Tab>
                <Tab
                  className={({ selected }) =>
                    classNames(
                      "tab px-8 py-2 text-sm font-medium rounded-full transition-colors duration-150 ease-in-out",
                      selected
                        ? "bg-redCrimson text-white shadow-md"
                        : "bg-white text-redCrimson hover:bg-red-50",
                    )
                  }
                  onClick={() => setOrderMode("Delivery")}
                >
                  Delivery
                </Tab>
              </Tab.List>
            </Tab.Group>
          ) : null}
        </div>

        {cart.length === 0 ? (
          <div className="flex flex-col items-center justify-between px-4">
            <div className="flex flex-col items-center justify-center h-[60vh]">
              <p className="font-bold text-xl">Your cart is empty.</p>
            </div>
            <Button
              variant="secondary"
              text="Add More Items"
              className="w-full h-[3rem] md:h-[3.4rem] rounded-[6px] md:rounded-[8px] text-[16px] md:text-[18px]
                              font-bold flex items-center justify-center"
              to={`${merchantSlug}`}
            />
          </div>
        ) : (
          <div className="space-y-4 mx-4">
            {adjustedCart.map((item) => (
              <div key={item.uniqueId} className="cart-item">
                <img
                  src={item.image}
                  alt={item.name}
                  className="product-image"
                />
                <div className="details">
                  <h2>{item.name}</h2>
                  <p>
                    $
                    {item.unit_price === "0.00"
                      ? productPrice(item)
                      : item.unit_price}
                  </p>
                  <div className="options">
                    {Object.entries(item.selected_options).map(
                      ([optionId, option]) =>
                        option.selected && (
                          <div key={optionId}>
                            {option.quantity} x {option.name}{" "}
                            {option.surcharge !== undefined &&
                              `(+${(option.surcharge * option.quantity).toFixed(
                                2,
                              )})`}
                          </div>
                        ),
                    )}
                  </div>
                </div>
                {!hasServiceInCart && !item.is_free_item && (
                  <div className="actions self-end md:self-center mt-4 md:mt-0">
                    <div className="quantity flex items-center">
                      <button
                        className="quantity-btn"
                        onClick={() =>
                          item.quantity > 1
                            ? handleQuantityChange(item.uniqueId, false)
                            : handleRemove(item.uniqueId)
                        }
                        disabled={item.quantity < 1}
                      >
                        -
                      </button>
                      <span className="quantity-text mx-2">
                        {item.quantity}
                      </span>
                      <button
                        className="quantity-btn"
                        onClick={() =>
                          handleQuantityChange(item.uniqueId, true)
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {cart.length > 0 && (
          <div className="flex flex-col">
            <div className="w-full items-center mx-auto my-2">
              <div className="flex justify-between mx-4">
                <div>Subtotal</div>
                <div>${subtotal.toFixed(2)}</div>
              </div>
            </div>

            <div className="flex flex-col items-center justify-center px-4 gap-5">
              <Button
                variant="primary"
                text="Checkout"
                className="w-full h-[3rem] md:h-[3.4rem] rounded-[6px] md:rounded-[8px] text-[16px] md:text-[18px] font-bold flex items-center justify-center"
                onClick={handleCheckoutButtonClick}
              />
              {!hasServiceInCart && (
                <Button
                  variant="secondary"
                  text="Add More Items"
                  className="w-full h-[3rem] md:h-[3.4rem] rounded-[6px] md:rounded-[8px] text-[16px] md:text-[18px] font-bold flex items-center justify-center"
                  to={`${merchantSlug}`}
                />
              )}
              <Button
                variant="tertiary"
                text="Clear Cart"
                className="w-full h-[3rem] md:h-[3.4rem] rounded-[6px] md:rounded-[8px] text-[16px] md:text-[18px] font-bold flex items-center justify-center"
                onClick={handleClearCart}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
